/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../../app/nls/StringResources'
], function(_, PropertiesProviderAPI, APIFactory, ClassFactory, StringResources) {

	class DashboardPropertiesProvider {

		constructor(options) {
			this._api = APIFactory.createAPI(this, [PropertiesProviderAPI]);
			this._properties = options.features.Properties;
			this._internal = options.features.internal;
			// TODO - we should not depend on the dashboard API in here
			this.dashboard = options.features.API;
		}

		getAPI() {
			return this._api;
		}

		initialize() {
			// register self as a properties provider contributing align properties
			this._properties.registerProvider(this._api);

			return this._fetchThemes();
		}

		destroy() {
			if (this._properties) {
				this._properties.deregisterProvider(this._api);
			}
		}

		getPropertyLayoutList(){
			return [{
				'value': StringResources.get('dashboardProperties'),
				'id': 'banner',
				'type': 'Banner',
				'editable': false
			}, {
				id: 'general',
				type: 'Group',
				label: StringResources.get('tabName_general')
			}, {
				id: 'colorsAndTheme',
				type: 'Section',
				label: StringResources.get('sectionName_colorsAndThemes'),
				position: 3
			}];
		}

		getPropertyList() {
			const internalApi = this._internal;
			const boardModel = internalApi.getBoardModel();
			const propertiesModel = boardModel.properties;

			return [{
				id: 'theme',
				setPropertyValue: value => { boardModel.set({ 'theme': value }); },
				getPropertyValue: () =>  { return boardModel.get('theme'); },
				editor: {
					sectionId: 'general.colorsAndTheme',
					position: 1,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTheme'),
						getOptions: () => this._themeListing
					}
				}
			}, {
				id: 'dashboardColorSet',
				setPropertyValue: value => { propertiesModel.set({ 'dashboardColorSet': value }); },
				getPropertyValue: () => { return propertiesModel.get('dashboardColorSet') || 'userPaletteColor'; },
				editor: {
					sectionId: 'general.colorsAndTheme',
					position: 1,
					uiControl: {
						type: 'NewPalette',
						paletteType: 'ColorPalette',
						sectionLabel: StringResources.get('propDashboardColorPalette'),
						newPaletteLabel: StringResources.get('propNewDashboardPaletteLabel'),
						linkLabel: StringResources.get('propDashboardChangePaletteLink'),
						ariaLabel: StringResources.get('propDashboardColorPalette'),
						includeUserColorPalette: true,
						onChange: (propertyName, propertyValue) => {
							this.setPropertyValue(propertyName, propertyValue);
							this.dashboard.triggerDashboardEvent('properties:refreshPane', {
								sender: propertiesModel.id
							});
						}
					}
				}
			}, {
				id: 'fredIsRed',
				setPropertyValue: value => { propertiesModel.set({ 'fredIsRed': value }); },
				getPropertyValue: () => { return propertiesModel.get('fredIsRed'); },
				validatePropertyValue: (value) => {
					return {
						isValid: typeof value === 'boolean',
						message: 'fredIsRed property must be of type boolean.'
					};
				},
				editor: {
					sectionId: 'general.advanced',
					position: 1,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propFredIsRed')
					}
				}
			}, {
				id: 'dataCache',
				setPropertyValue: (value) => {
					const modelledValue = value === 'automatic' ? undefined : value;
					propertiesModel.set({
						'localCache': modelledValue
					});
				},
				getPropertyValue: () => { return propertiesModel.get('localCache') || 'automatic'; },
				editor: {
					sectionId: 'general.advanced',
					position: 2,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('dataCache'),
						options: [{
							label: StringResources.get('dataCache_auto'),
							value: 'automatic'
						}, {
							label: StringResources.get('dataCache_on'),
							value: 'yes'
						}, {
							label: StringResources.get('dataCache_off'),
							value: 'no'
						}]
					}
				}
			}];
		}

		_fetchThemes() {
			if (!this._themeListing) {
				this._themeListing = [];
				return ClassFactory
					.loadModule('text!dashboard-core/js/lib/@waca/dashboard-common/dist/themes/themeListing.json')
					.then((themeListing) => {
						const themesInfo = JSON.parse(themeListing);

						_.each(themesInfo.themes, (theme) => {
							this._themeListing.push({
								label: StringResources.get(theme.name + 'Label'),
								value: theme.name
							});
						});

						return this._themeListing;
					}, () => {
						this._themeListing = [];
						return this._themeListing;
					});
			} else {
				return Promise.resolve(this._themeListing);
			}
		}
	}

	return DashboardPropertiesProvider;

});
